import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useAuthStore } from '../../../stores/auth.store';
import { useRecordWaste } from '../../../api/mutations/waste.mutations';
import { useInventoryItems } from '../../../api/queries/inventory.queries';
import { FileUpload } from '../../../components/FileUpload';

interface RecordWasteModalProps {
  onClose: () => void;
}

function RecordWasteModal({ onClose }: RecordWasteModalProps) {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const recordMutation = useRecordWaste(user?.branchId || '');
  const { data: inventoryItems } = useInventoryItems(user?.branchId);

  const [ingredientId, setIngredientId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [category, setCategory] = useState<'expired' | 'damaged' | 'spillage' | 'quality_issue' | 'over_production' | 'other'>('expired');
  const [reason, setReason] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');

  const selectedIngredient = inventoryItems?.find((item) => item.ingredientId === ingredientId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await recordMutation.mutateAsync({
        ingredientId,
        quantity: parseFloat(quantity),
        category,
        reason,
        photoUrl: photoUrl || undefined,
      });
      onClose();
    } catch (error) {
      console.error('Failed to record waste:', error);
    }
  };

  const categories = [
    { value: 'expired', label: t('waste.expired') },
    { value: 'damaged', label: t('waste.damaged') },
    { value: 'spillage', label: t('waste.spillage') },
    { value: 'quality_issue', label: t('waste.qualityIssue') },
    { value: 'over_production', label: t('waste.overProduction') },
    { value: 'other', label: t('waste.other') },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-lg rounded-lg bg-card">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">{t('waste.record')}</h2>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-secondary tap-highlight-none"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Ingredient selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('inventory.ingredient')}</label>
            <select
              value={ingredientId}
              onChange={(e) => setIngredientId(e.target.value)}
              required
              className="w-full rounded-lg border bg-background px-3 py-2"
            >
              <option value="">{t('waste.selectIngredient') || 'Select ingredient...'}</option>
              {inventoryItems?.map((item) => (
                <option key={item.ingredientId} value={item.ingredientId}>
                  {item.ingredientName} ({item.currentStock.toFixed(2)} {item.unit})
                </option>
              ))}
            </select>
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('inventory.quantity')}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                step="0.01"
                min="0"
                max={selectedIngredient?.currentStock}
                required
                className="flex-1 rounded-lg border bg-background px-3 py-2"
                placeholder="0.00"
              />
              {selectedIngredient && (
                <span className="text-muted-foreground">{selectedIngredient.unit}</span>
              )}
            </div>
            {selectedIngredient && (
              <p className="text-xs text-muted-foreground">
                {t('waste.available') || 'Available'}: {selectedIngredient.currentStock.toFixed(2)} {selectedIngredient.unit}
              </p>
            )}
          </div>

          {/* Category */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('waste.category')}</label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value as any)}
                  className={`rounded-lg border py-2 px-3 text-sm transition-colors ${
                    category === cat.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background hover:bg-secondary'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('inventory.reason')}</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              className="w-full rounded-lg border bg-background px-3 py-2 min-h-[80px]"
              placeholder={t('waste.reasonPlaceholder') || 'Describe the waste reason...'}
            />
          </div>

          {/* Photo Upload */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('waste.photo') || 'Photo Evidence (Optional)'}</label>
            <FileUpload
              folder="waste"
              onUploadComplete={(url) => setPhotoUrl(url)}
              onUploadError={(error) => console.error('Upload error:', error)}
              label={t('waste.uploadPhoto') || 'Upload Photo'}
              description={t('waste.uploadDescription') || 'Click to upload or drag and drop'}
              maxSizeMB={10}
            />
            {photoUrl && (
              <p className="text-xs text-green-600 dark:text-green-400">
                ✓ {t('waste.photoUploaded') || 'Photo uploaded successfully'}
              </p>
            )}
          </div>

          {/* Warning for large amounts */}
          {selectedIngredient && parseFloat(quantity) > selectedIngredient.currentStock * 0.2 && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 dark:bg-orange-950/20 p-3 text-sm text-orange-800 dark:text-orange-200">
              ⚠️ {t('waste.largeAmountWarning') || 'Large waste amount detected. Supervisor approval will be required.'}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border bg-background px-4 py-2 hover:bg-secondary transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={recordMutation.isPending || !ingredientId || !quantity || !reason}
              className="flex-1 rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {recordMutation.isPending ? t('common.loading') : t('waste.record')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RecordWasteModal;
